import { useState, useRef, useEffect, useMemo } from 'react';

/**
 * Hook that smoothly animates an element's height when its content changes.
 * Returns a style object to apply to a wrapper div around the measured content.
 *
 * @param {React.RefObject} contentRef - ref attached to the inner content element
 * @returns {object} style object with animated height + transition
 */
export const useAnimatedHeight = (contentRef) => {
  const [measuredHeight, setMeasuredHeight] = useState(null);
  const prevHeight = useRef(null);
  const shouldAnimate = useRef(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setMeasuredHeight(el.scrollHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentRef]);

  // Track whether we should animate (skip the first measurement)
  useEffect(() => {
    if (measuredHeight == null) return;
    shouldAnimate.current = prevHeight.current != null;
    prevHeight.current = measuredHeight;
  }, [measuredHeight]);

  const style = useMemo(() => {
    if (measuredHeight == null) return {};
    return {
      height: measuredHeight,
      transition: shouldAnimate.current
        ? 'height 0.25s cubic-bezier(0.25, 1, 0.4, 1)'
        : 'none',
    };
  }, [measuredHeight]);

  return style;
};
